import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";

import { UploadOutlined } from "@ant-design/icons";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Form,
  Input,
  Row,
  Col,
  Space,
  App,
  Select,
  DatePicker,
  Upload,
  Typography,
  Spin,
  UploadProps,
} from "antd";
import dayjs, { Dayjs } from "dayjs";

import { leaveTypeLabels } from "../../../constant/enum";
import { countDateDays } from "../../../utils/countDateUtils";
import {
  createLeave,
  editLeave,
  getUserLeavesBalanceByType,
  checkOverlappingLeaves,
  downloadLeaveFileFromS3,
} from "../api/leaveAPI";
import { ILeaveRequest, LeaveRequestSchema } from "../type/leave";

const { TextArea } = Input;
const { Text } = Typography;

type TimePeriod = "full" | "halfDay";

interface LeaveFormProps {
  initialData?: ILeaveRequest;
  mode?: "create" | "update";
  leaveId?: string;
}

export const LeaveForm = ({
  initialData,
  mode = "create",
  leaveId,
}: LeaveFormProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message: messageApi } = App.useApp();

  // Convert initial dates from string to Dayjs for state and form
  const initialStartDate = initialData?.startDate
    ? dayjs(initialData.startDate)
    : null;
  const initialEndDate = initialData?.endDate
    ? dayjs(initialData.endDate)
    : null;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [startTimePeriod, setStartTimePeriod] = useState<TimePeriod>("full");
  const [endTimePeriod, setEndTimePeriod] = useState<TimePeriod>("full");
  const [startDate, setStartDate] = useState<Dayjs | null>(initialStartDate);
  const [endDate, setEndDate] = useState<Dayjs | null>(initialEndDate);
  const [selectedLeaveType, setSelectedLeaveType] = useState<string>(
    initialData?.leaveType || "annual"
  );

  // Initialize file list with existing file URL if available
  const [fileList, setFileList] = useState<any[]>(() => {
    if (initialData?.fileUrl) {
      // Extract filename from URL
      const urlParts = initialData.fileUrl.split("/");
      const fileName = urlParts[urlParts.length - 1];

      return [
        {
          uid: "-1",
          name: fileName || "Existing document",
          status: "done",
          s3Path: initialData.fileUrl,
          isExisting: true,
        },
      ];
    }
    return [];
  });

  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [dateOverlapError, setDateOverlapError] = useState<string>("");

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ILeaveRequest>({
    resolver: zodResolver(LeaveRequestSchema),
    defaultValues: {
      leaveType: initialData?.leaveType || "annual",
      startDate: initialStartDate?.toDate() || undefined,
      endDate: initialEndDate?.toDate() || undefined,
      reason: initialData?.reason || undefined,
      totalDays: initialData?.totalDays || undefined,
    },
  });

  // Fetch user's leave balance using Tanstack Query
  const { data: balanceData, isLoading: isLoadingBalance } = useQuery({
    queryKey: ["userLeaveBalance", selectedLeaveType],
    queryFn: () => getUserLeavesBalanceByType(selectedLeaveType),
    enabled: !!selectedLeaveType,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });

  // Calculate days allowed based on selected leave type
  const daysAllowed = useMemo(() => {
    return balanceData?.balance || 0;
  }, [balanceData]);

  // Calculate days required using useMemo for better performance
  const daysRequired = useMemo(() => {
    return countDateDays(startDate, endDate, startTimePeriod, endTimePeriod);
  }, [startDate, endDate, startTimePeriod, endTimePeriod]);

  // Check for overlapping leaves when dates change
  const checkDateOverlap = async () => {
    if (!startDate || !endDate) {
      setDateOverlapError("");
      return;
    }

    try {
      // Format dates as date-only strings to avoid timezone conversion issues
      const result = await checkOverlappingLeaves(
        startDate.format("YYYY-MM-DD"),
        endDate.format("YYYY-MM-DD"),
        leaveId // Pass leaveId when updating to exclude current leave
      );

      if (result.hasOverlap) {
        setDateOverlapError(
          "The selected dates overlap with an existing leave application. Please choose different dates."
        );
      } else {
        setDateOverlapError("");
      }
    } catch (error: any) {
      console.error("Error checking date overlap:", error);
    }
  };

  // Call checkDateOverlap whenever dates change
  useMemo(() => {
    if (startDate && endDate) {
      checkDateOverlap();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const props: UploadProps = {
    name: "file",
    multiple: false,
    fileList: fileList,
    onPreview: async (file: any) => {
      if (file.isExisting && file.s3Path && leaveId) {
        try {
          await downloadLeaveFileFromS3(leaveId, file.s3Path);
        } catch (error) {
          messageApi.error("Failed to open the file");
        }
      } else if (file.url && !file.isExisting) {
        window.open(file.url);
      }
    },
    showUploadList: {
      showPreviewIcon: false,
      showRemoveIcon: true,
    },
    beforeUpload: (file) => {
      // limit file size to 50MB
      if (file.size > 50 * 1024 * 1024) {
        messageApi.error("File must be smaller than 50MB!");
        return Upload.LIST_IGNORE;
      }

      // Set the new uploaded file
      setUploadedFile(file);

      // Replace the file list with the new file (removes old file if exists)
      setFileList([
        {
          uid: file.uid || "-1",
          name: file.name,
          status: "done",
          url: URL.createObjectURL(file),
        },
      ]);

      return false; // Prevent automatic upload
    },
    onRemove: () => {
      setUploadedFile(null);
      setFileList([]);
    },
    onChange: ({ fileList: newFileList }) => {
      // Update file list when files change
      setFileList(newFileList);
    },
    accept: ".pdf,.doc,.docx,.jpg,.jpeg,.png",
    maxCount: 1, // Only allow one file at a time
  };

  const onSubmit = async (data: ILeaveRequest) => {
    if (daysRequired < 0.5) {
      messageApi.error("Please select valid dates for your leave");
      return;
    }

    if (dateOverlapError) {
      messageApi.error(dateOverlapError);
      return;
    }

    if (daysRequired > daysAllowed && selectedLeaveType !== "unpaid") {
      messageApi.error(
        `Insufficient leave balance. You have ${daysAllowed} days remaining.`
      );
      return;
    }

    setIsSubmitting(true);
    try {
      // Data.startDate and data.endDate are already Date objects from Zod transformer
      // Use current state dates if they exist, otherwise use form data
      const startDateToUse = startDate
        ? startDate.toDate()
        : dayjs(data.startDate).toDate();
      const endDateToUse = endDate
        ? endDate.toDate()
        : dayjs(data.endDate).toDate();

      // Set time based on period selection
      if (startTimePeriod === "halfDay") {
        startDateToUse.setHours(8, 0, 0, 0);
      } else {
        startDateToUse.setHours(8, 0, 0, 0); // Full day starts at 8am
      }

      if (endTimePeriod === "halfDay") {
        endDateToUse.setHours(12, 0, 0, 0); // Half day ends at noon
      } else {
        endDateToUse.setHours(18, 0, 0, 0); // Full day ends at 6pm
      }

      const submitData = {
        ...data,
        startDate: startDateToUse,
        endDate: endDateToUse,
        totalDays: daysRequired,
      };

      if (mode === "create") {
        await createLeave(submitData, uploadedFile || undefined);
        messageApi.success("Leave application submitted successfully!");
      } else {
        if (leaveId) {
          await editLeave(leaveId, submitData, uploadedFile || undefined);
          messageApi.success("Leave application updated successfully!");
          // Invalidate the specific leave query so next edit shows fresh data
          queryClient.invalidateQueries({ queryKey: ["leave", leaveId] });
          // Invalidate leave balance in case total days changed
          queryClient.invalidateQueries({ queryKey: ["userLeaveBalance"] });
        }
      }
      navigate(-1);
    } catch (error: any) {
      messageApi.error(error.message || "An error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <Form
      layout="vertical"
      onFinish={handleSubmit(onSubmit, () => {
        messageApi.error("Please check all required fields");
      })}
    >
      <Row gutter={[24, 16]}>
        {/* ================ Leave Type Field ================ */}
        <Col xs={24}>
          <Form.Item
            label="Leave Type"
            validateStatus={errors.leaveType ? "error" : ""}
            help={errors.leaveType?.message}
            style={{ marginBottom: 24 }}
            required
          >
            <Controller
              name="leaveType"
              control={control}
              render={({ field }) => (
                <Select
                  placeholder="Select Leave Type"
                  {...field}
                  size="large"
                  style={{ width: "100%" }}
                  onChange={(value) => {
                    field.onChange(value);
                    setSelectedLeaveType(value);
                  }}
                  options={[
                    { value: "annual", label: leaveTypeLabels.annual },
                    { value: "medical", label: leaveTypeLabels.medical },
                    {
                      value: "hospitalization",
                      label: leaveTypeLabels.hospitalization,
                    },
                    { value: "maternity", label: leaveTypeLabels.maternity },
                    { value: "unpaid", label: leaveTypeLabels.unpaid },
                    {
                      value: "compassionate",
                      label: leaveTypeLabels.compassionate,
                    },
                    { value: "marriage", label: leaveTypeLabels.marriage },
                  ]}
                />
              )}
            />
          </Form.Item>
        </Col>

        {/* ================ Select Dates Section ================ */}
        <Col xs={24}>
          <Text type="danger">* </Text>
          <Text style={{ fontSize: 16 }}>Select dates</Text>
          {dateOverlapError && (
            <div style={{ marginTop: 8 }}>
              <Text type="danger" style={{ fontSize: 14 }}>
                {dateOverlapError}
              </Text>
            </div>
          )}
          <Row gutter={16} style={{ marginTop: 12 }} align="middle">
            {/* Start Date */}
            <Col xs={24} sm={11}>
              <Row gutter={8}>
                <Col span={14}>
                  <Form.Item
                    validateStatus={errors.startDate ? "error" : ""}
                    help={errors.startDate?.message}
                    style={{ marginBottom: 0 }}
                  >
                    <Controller
                      name="startDate"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          placeholder="Select Start Date"
                          {...field}
                          value={startDate}
                          onChange={(date) => {
                            field.onChange(date);
                            setStartDate(date);
                          }}
                          size="large"
                          format="DD/MM/YYYY"
                          style={{
                            width: "100%",
                            borderColor: errors.startDate
                              ? "#ff4d4f"
                              : "#a6a6a6",
                          }}
                          // disabledDate={(current) => {
                          //   if (selectedLeaveType === "medical") {
                          //     return false;
                          //   }
                          //   return current && current < dayjs().startOf("day");
                          // }}
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={10}>
                  <Form.Item
                    style={{ marginBottom: 0 }}
                    help={
                      errors.startDate || errors.endDate ? (
                        <span style={{ visibility: "hidden" }}>
                          placeholder
                        </span>
                      ) : undefined
                    }
                  >
                    <Select
                      value={startTimePeriod}
                      onChange={setStartTimePeriod}
                      size="large"
                      style={{ width: "100%" }}
                      options={[
                        { value: "full", label: "full day" },
                        { value: "halfDay", label: "half day" },
                      ]}
                      suffixIcon={<span style={{ fontSize: 12 }}>▼</span>}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Col>

            {/* "to" text */}
            <Col xs={24} sm={2} style={{ textAlign: "center" }}>
              <Form.Item
                style={{ marginBottom: 0 }}
                help={
                  errors.startDate || errors.endDate ? (
                    <span style={{ visibility: "hidden" }}>placeholder</span>
                  ) : undefined
                }
              >
                <Text strong style={{ fontSize: 16 }}>
                  to
                </Text>
              </Form.Item>
            </Col>

            {/* End Date */}
            <Col xs={24} sm={11}>
              <Row gutter={8}>
                <Col span={14}>
                  <Form.Item
                    validateStatus={errors.endDate ? "error" : ""}
                    help={errors.endDate?.message}
                    style={{ marginBottom: 0 }}
                  >
                    <Controller
                      name="endDate"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          placeholder="Select End Date"
                          {...field}
                          value={endDate}
                          onChange={(date) => {
                            field.onChange(date);
                            setEndDate(date);
                          }}
                          size="large"
                          format="DD/MM/YYYY"
                          style={{
                            width: "100%",
                            borderColor: errors.endDate ? "#ff4d4f" : "#a6a6a6",
                          }}
                          disabledDate={(current) => {
                            if (selectedLeaveType === "medical") {
                              if (startDate) {
                                return current && current < startDate;
                              }
                              return false;
                            }
                            if (!startDate)
                              return (
                                current && current < dayjs().startOf("day")
                              );
                            return current && current < startDate;
                          }}
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={10}>
                  <Form.Item
                    style={{ marginBottom: 0 }}
                    help={
                      errors.startDate || errors.endDate ? (
                        <span style={{ visibility: "hidden" }}>
                          placeholder
                        </span>
                      ) : undefined
                    }
                  >
                    <Select
                      value={endTimePeriod}
                      onChange={setEndTimePeriod}
                      size="large"
                      style={{ width: "100%" }}
                      options={[
                        { value: "full", label: "full day" },
                        { value: "halfDay", label: "half day" },
                      ]}
                      suffixIcon={<span style={{ fontSize: 12 }}>▼</span>}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>

        {/* ================ Days Display ================ */}
        <Col xs={24}>
          <Row gutter={48} style={{ marginTop: 24, marginBottom: 24 }}>
            <Col xs={12} style={{ textAlign: "center" }}>
              <div style={{ fontSize: 48 }}>{daysRequired}</div>
              <Text style={{ fontSize: 16, color: "#8c8c8c" }}>
                days required
              </Text>
            </Col>
            <Col xs={12} style={{ textAlign: "center" }}>
              {isLoadingBalance ? (
                <div
                  style={{
                    fontSize: 50,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 50,
                  }}
                >
                  <Spin />
                </div>
              ) : (
                <div style={{ fontSize: 48 }}>
                  {daysAllowed === 0 ? (
                    <span>&infin;</span>
                  ) : (
                    <span>{daysAllowed}</span>
                  )}
                </div>
              )}
              <Text style={{ fontSize: 16, color: "#8c8c8c" }}>
                days allowed
              </Text>
            </Col>
          </Row>
        </Col>

        {/* ================ Other Details Field ================ */}
        <Col xs={24}>
          <Form.Item
            label="Other Details"
            name="reason"
            validateStatus={errors.reason ? "error" : ""}
            help={errors.reason?.message}
            required
          >
            <Controller
              name="reason"
              control={control}
              render={({ field }) => (
                <TextArea
                  placeholder="Enter any additional details"
                  {...field}
                  rows={4}
                />
              )}
            />
          </Form.Item>
        </Col>

        {/* ================ Upload File Field ================ */}
        <Col xs={24}>
          <Form.Item
            label="Upload Document"
            name="fileUpload"
            tooltip="Allowed file types: PDF, DOC, DOCX, JPG, JPEG, PNG"
          >
            <div className="custom-upload-list">
              <Upload {...props}>
                <Button
                  icon={<UploadOutlined />}
                  size="large"
                  style={{
                    backgroundColor: "#5b8db8",
                    color: "white",
                    border: "none",
                    borderRadius: 4,
                    fontWeight: 500,
                  }}
                >
                  Select Files
                </Button>
              </Upload>
            </div>
          </Form.Item>
        </Col>
      </Row>

      {/* ================ Form Actions ================ */}
      <Form.Item>
        <Row justify="end">
          <Space
            direction="horizontal"
            wrap
            style={{ display: "flex", justifyContent: "flex-end" }}
          >
            <Button
              style={{ margin: 5 }}
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              style={{ margin: 5 }}
              type="primary"
              htmlType="submit"
              loading={isSubmitting}
            >
              {mode === "create" ? "Submit Leave" : "Update Leave"}
            </Button>
          </Space>
        </Row>
      </Form.Item>
    </Form>
  );
};
